/* eslint-disable react-refresh/only-export-components */
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

const ShortcutContext = createContext(null);

export const ShortcutProvider = ({ children }) => {
   const shortcutStack = useRef([]);

   const register = useCallback((id, keyMap, options = {}) => {
      // Add or update the shortcut in the stack
      const existingIndex = shortcutStack.current.findIndex(item => item.id === id);
      const newEntry = { id, keyMap, options };

      if (existingIndex !== -1) {
         shortcutStack.current[existingIndex] = newEntry;
      } else {
         shortcutStack.current.push(newEntry);
      }
   }, []);

   const unregister = useCallback(id => {
      shortcutStack.current = shortcutStack.current.filter(item => item.id !== id);
   }, []);

   const getAction = (keyMap, e) => {
      // 1. Direct match (e.g., "Escape", "ArrowLeft")
      const key = e.key === 'Esc' ? 'Escape' : e.key;
      if (keyMap[key]) return keyMap[key];
      if (keyMap[e.keyCode]) return keyMap[e.keyCode];

      // 2. Modifier combos (e.g., "Control+k", "Meta+k", "Shift+ArrowUp")
      const modifiers = [];
      if (e.ctrlKey) modifiers.push('Control');
      if (e.metaKey) modifiers.push('Meta');
      if (e.altKey) modifiers.push('Alt');
      if (e.shiftKey) modifiers.push('Shift');

      if (modifiers.length > 0) {
         const combo = modifiers.join('+') + '+' + e.key;
         if (keyMap[combo]) return keyMap[combo];

         // Support universal "CmdOrCtrl" shortcut key
         if (e.ctrlKey || e.metaKey) {
            const universalMods = modifiers.filter(m => m !== 'Control' && m !== 'Meta');
            universalMods.unshift('CmdOrCtrl');
            const universalCombo = universalMods.join('+') + '+' + e.key;
            if (keyMap[universalCombo]) return keyMap[universalCombo];
         }
      }
      return null;
   };

   const handleGlobalKeyDown = useCallback(e => {
      // Detect if user is typing in an input or contenteditable element
      const activeEl = document.activeElement;
      const isTyping = ['INPUT', 'TEXTAREA'].includes(activeEl.tagName) || activeEl.isContentEditable;

      // Iterate from the top of the stack (most recently registered has priority)
      for (let i = shortcutStack.current.length - 1; i >= 0; i--) {
         const { keyMap, options } = shortcutStack.current[i];

         // Standard override: if typing, ignore common navigation keys unless specifically allowed
         const isNavKey = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key);
         if (isTyping && isNavKey && !options.allowWhileTyping) {
            continue;
         }

         const action = getAction(keyMap, e);
         if (action) {
            const result = action(e);
            // If the action returned false, it means it didn't consume the event.
            // Continue searching the stack for the next handler.
            if (result !== false) {
               if (options.preventDefault !== false) {
                  e.preventDefault();
               }
               return; // Consumed
            }
         }
      }
   }, []);

   useEffect(() => {
      window.addEventListener('keydown', handleGlobalKeyDown);
      return () => window.removeEventListener('keydown', handleGlobalKeyDown);
   }, [handleGlobalKeyDown]);

   const contextValue = useMemo(() => ({ register, unregister }), [register, unregister]);

   return <ShortcutContext.Provider value={contextValue}>{children}</ShortcutContext.Provider>;
};

ShortcutProvider.propTypes = {
   children: PropTypes.node.isRequired,
};

export const useShortcutRegistry = () => useContext(ShortcutContext);
